import { BarChartOutlined, TableOutlined } from '@ant-design/icons';
import { Card, Spin, Typography } from 'antd';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { BRAND_COLORS } from '../../../../constants/styles';
import { STATUS_COLORS } from '../../constants';
import { TrendDirection } from '../../types';
import { SkewedBadge } from '../SkewedBadge';
import { TabLinkButton } from '../TabLinkButton';
import { TrendIndicator } from '../TrendIndicator';

const { Text } = Typography;

const DonorTypeRow: FC<{
  badge: string;
  badgeColor: string;
  borderColor: string;
  label: string;
  count: number;
  loading: boolean;
  tooltip: string;
  contentWidth: number;
  onListClick: () => void;
}> = ({
  badge,
  badgeColor,
  borderColor,
  label,
  count,
  loading,
  tooltip,
  contentWidth,
  onListClick,
}) => {
  const { t } = useTranslation('dashboard.RecruiterDashboard.showRatesCard');
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        background: 'rgba(255,255,255,0.1)',
        borderRadius: 8,
        padding: '10px 14px',
      }}
    >
      <div
        style={{
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: badgeColor,
          border: `3px solid ${borderColor}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Text
          style={{
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          {badge}
        </Text>
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Text
            style={{ color: BRAND_COLORS.secondary.lightSage, fontSize: 11 }}
          >
            {label}
          </Text>
          <span style={{ color: '#fff', fontSize: 24, fontWeight: 700 }}>
            {loading ? <Spin size="small" /> : count}
          </span>
        </div>
        <TabLinkButton
          fontSize={11}
          iconColor={badgeColor}
          icon={<TableOutlined />}
          label={t('donorListLabel')}
          onClick={onListClick}
          tooltip={tooltip}
          windowWidth={contentWidth}
        />
      </div>
    </div>
  );
};

export interface IShowRatesDblUnitCardProps {
  metricsLoading: boolean;
  showRate: number;
  showRateStatus: { color: string; border: string; label: string };
  showRateTrend: TrendDirection;
  showRateTrendColor: string;
  singleDonorCount: number;
  doubleDonorCount: number;
  contentWidth: number;
  onShowRateDetailsClick: () => void;
  onSingleDonorsClick: () => void;
  onDoubleDonorsClick: () => void;
}

export const ShowRatesDblUnitCard: FC<IShowRatesDblUnitCardProps> = ({
  metricsLoading,
  showRate,
  showRateStatus,
  showRateTrend,
  showRateTrendColor,
  singleDonorCount,
  doubleDonorCount,
  contentWidth,
  onShowRateDetailsClick,
  onSingleDonorsClick,
  onDoubleDonorsClick,
}) => {
  const { t } = useTranslation('dashboard.RecruiterDashboard.showRatesCard');
  return (
    <Card
      size="small"
      style={{ flex: 1, background: BRAND_COLORS.primary.deepNavy }}
    >
      <Text style={{ color: '#fff', display: 'block', textAlign: 'center' }}>
        {t('title')}
      </Text>
      <div style={{ marginTop: 8 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            background: BRAND_COLORS.primary.royalBlue,
            border: `1px solid ${BRAND_COLORS.secondary.steelBlue}`,
            borderRadius: 6,
            padding: 16,
            gap: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '8px 0',
            }}
          >
            <Text
              style={{
                color: BRAND_COLORS.secondary.lightSage,
                fontSize: 13,
                marginBottom: 0,
              }}
            >
              {t('showRateLabel')}
            </Text>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  style={{
                    color: showRateStatus.color,
                    fontSize: 40,
                    fontWeight: 700,
                  }}
                >
                  {metricsLoading ? (
                    <Spin size="small" />
                  ) : (
                    `${Math.round(showRate)}%`
                  )}
                </span>
                <TrendIndicator
                  direction={showRateTrend}
                  color={showRateTrendColor}
                  label={showRateTrend.toUpperCase()}
                />
              </div>
              <SkewedBadge
                color={showRateStatus.color}
                border={showRateStatus.border}
                label={showRateStatus.label}
              />
              <div style={{ marginTop: 8 }}>
                <TabLinkButton
                  onClick={onShowRateDetailsClick}
                  fontSize={10}
                  iconColor={showRateStatus.color}
                  icon={<BarChartOutlined />}
                  label={t('detailsLabel')}
                  tooltip={t('detailsTooltip')}
                  windowWidth={contentWidth}
                />
              </div>
            </div>
          </div>

          <div
            style={{
              height: 1,
              background: 'rgba(255,255,255,0.2)',
              marginLeft: '10%',
              marginRight: '10%',
            }}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Text
              style={{
                color: BRAND_COLORS.secondary.lightSage,
                fontSize: 13,
                textAlign: 'center',
              }}
            >
              {t('dblSglLabel')}
            </Text>
            <DonorTypeRow
              badge="SGL"
              badgeColor={STATUS_COLORS.ok.fill}
              borderColor={STATUS_COLORS.ok.border}
              label={t('singleDonorsLabel')}
              count={singleDonorCount}
              loading={metricsLoading}
              tooltip={t('singleDonorsTooltip')}
              contentWidth={contentWidth}
              onListClick={onSingleDonorsClick}
            />
            <DonorTypeRow
              badge="DBL"
              badgeColor={STATUS_COLORS.good.fill}
              borderColor={STATUS_COLORS.good.border}
              label={t('doubleDonorsLabel')}
              count={doubleDonorCount}
              loading={metricsLoading}
              tooltip={t('doubleDonorsTooltip')}
              contentWidth={contentWidth}
              onListClick={onDoubleDonorsClick}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};
